<template>
  <div>
    <v-dialog v-model="showDialog" fullscreen persistent hide-overlay>
      <template v-slot:activator="{ on, attrs }">
        <v-btn v-bind="attrs" v-on="on" color="primary">
          <v-icon>
            {{ icons.mdiUpload }}
          </v-icon>
          Patchliste hochladen
        </v-btn>
      </template>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="showDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            Patchliste hochladen | Projekt {{ project.projektId }} -
            {{ project.name }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              :loading="isSubmitting"
              :disabled="!isTableDataValid"
              dark
              text
              @click="showSubmitDialog = true"
            >
              <v-icon>
                {{ icons.mdiUpload }}
              </v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <v-file-input
            ref="filePicker"
            v-model="file"
            class="my-4"
            counter
            show-size
            label="Patchliste auswählen"
            :accept="allowedExtensions.join(',')"
            :truncate-length="truncateLength"
            :disabled="false"
            :loading="isReadingFile || isSubmitting"
            @change="onFileChange"
          ></v-file-input>

          <v-alert
            v-if="errorMessage"
            elevation="0"
            :type="errorType ? errorType : 'error'"
            class="my-4"
            text
            outlined
          >
            <span v-html="errorMessage"></span>
          </v-alert>

          <v-layout column style="max-height: calc(100vh - 240px)">
            <v-flex
              style="overflow-y: auto; var(--goe-borderColor-dark) transparent;"
              class="custom-scrollbar"
            >
              <template v-if="errors.length > 0">
                <v-alert type="error" class="my-4" text outlined>
                  <h3>
                    Die Datei kann nicht importiert werden, folgende Probleme
                    wurden festgestellt:
                  </h3>
                  <br />
                  <ul>
                    <li v-for="(error, index) of errors" :key="`${index}error`">
                      <span v-html="error"></span>
                    </li>
                  </ul>
                </v-alert>
              </template>
              <v-data-table
                v-if="parsedFile && tableItems"
                :headers="tableHeaders"
                :items="tableItems"
                :loading="isSubmitting"
                :search="tableSearchTerm"
                :items-per-page.sync="tableItemsPerPage"
                disable-sort
                hide-default-header
                @update:items-per-page="
                  updateLocalStorage('itemsPerPage', $event)
                "
              >
                <template v-slot:top>
                  <v-toolbar flat>
                    <div
                      class="pa-2 d-flex justify-space-between align-center"
                      style="width: 100%"
                    >
                      <v-switch
                        v-model="showInvalidOnly"
                        class="mt-6"
                        style="display: flex; justify-content: center"
                        label="Fehlerhafte Einträge anzeigen"
                      ></v-switch>
                      <v-text-field
                        v-model="tableSearchTerm"
                        placeholder="Suche"
                        class="mt-5 px-9"
                      />
                    </div>
                  </v-toolbar>
                </template>
                <template #header="{ props: { headers } }">
                  <thead class="v-data-table-header">
                    <tr>
                      <th
                        v-for="header in headers"
                        :key="header.value"
                        class="text-start"
                        role="columnheader"
                        scope="col"
                        :aria-label="header.text"
                      >
                        <v-icon small left class="mr-1">
                          {{ getIconByType(header) }}
                        </v-icon>
                        {{ header.text }}
                        <template v-if="!selectedHeaders[header.value]">
                          <v-icon
                            v-if="!selectedHeaders[header.value]"
                            color="orange"
                            :title="`Es konnte keine Spalte für ${header.text} gefunden werden`"
                            small
                          >
                            {{ icons.mdiAlertCircle }}
                          </v-icon>
                        </template>
                        <v-icon
                          v-if="freeHeaders.length > 0"
                          class="justify-end"
                          icon
                          small
                          right
                          @click="selectHeaderForCol(header.value)"
                        >
                          {{ icons.mdiTableColumn }}
                        </v-icon>
                      </th>
                    </tr>
                  </thead>
                </template>
                <template #body="{ items, headers }">
                  <tbody>
                    <tr v-for="(item, index) in items" :key="index + '_row'">
                      <td
                        v-for="header in headers"
                        :key="index + header.value"
                        class="text-start"
                      >
                        <template v-if="selectedHeaders[header.value]">
                          <template
                            v-if="
                              (validationResult = isValueValid(
                                item[header.value],
                                header.value
                              ))
                            "
                          >
                            <v-icon
                              v-if="!validationResult[0]"
                              :color="
                                validationResult[1] ? 'primary' : 'orange'
                              "
                              :title="
                                validationResult[1]
                                  ? 'Optionaler Wert'
                                  : 'Ungültiger Wert'
                              "
                            >
                              {{ icons.mdiAlertCircle }}
                            </v-icon>
                          </template>
                          {{ item[header.value] }}
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            :loading="isSubmitting"
            :disabled="!isTableDataValid || showInvalidOnly"
            color="primary"
            @click="showSubmitDialog = true"
          >
            Importprozess starten
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSubmitDialog" width="500">
      <v-card :loading="isSubmitting">
        <v-card-title> Importprozess starten </v-card-title>

        <v-card-text>
          Soll der Import Prozess für <b>{{ tableItemsCount }}</b> Einträge in
          {{ project.projektId }} - {{ project.name }} gestartet werden?
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn @click="showSubmitDialog = false" color="secondary">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :loading="isSubmitting"
            :disabled="!isTableDataValid"
            color="primary"
            @click="submitData"
          >
            Ja, importieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="selectSheetOptions && selectSheetPromise"
      v-model="selectSheetPromise"
      width="500"
    >
      <v-card>
        <v-card-title>
          Das Dokument enthält mehrere Tabellenblätter
        </v-card-title>

        <v-card-text>
          <v-combobox
            v-model="selectedSheetValue"
            :items="selectSheetOptions"
            placeholder="Tabellenblatt auswählen"
          ></v-combobox>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn @click="userSelectWorksheet" color="secondary">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="selectSheetOptions && selectSheetPromise"
            color="primary"
            :disabled="!selectedSheetValue"
            @click="userSelectWorksheet"
          >
            Auswählen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="selectHeaderPromise"
      v-model="selectHeaderPromise"
      width="500"
    >
      <v-card>
        <v-card-title>
          Spalte für {{ columnNameForSelection }} auswählen
        </v-card-title>

        <v-card-text>
          <v-combobox
            v-model="selectedHeader"
            :items="freeHeaders"
            :placeholder="`Spalte für ${columnNameForSelection} auswählen`"
          ></v-combobox>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn @click="userSelectHeaderForCol" color="secondary">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="selectHeaderPromise"
            color="primary"
            :disabled="!selectedHeader"
            @click="userSelectHeaderForCol"
          >
            Auswählen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { HTTP } from '@/main/httpClient.js';
import {
  mdiTableColumn,
  mdiAlertCircle,
  mdiUpload,
  mdiFormatText,
  mdiNumeric
} from '@mdi/js';
import NotificationObject from '@/main/NotificationObject.js';

const EXCEL_EXTENSION = '.xlsx';
const CSV_EXTENSION = '.csv';

const DEFAULT_SHEET_NAME = 'FieldservicePatchListe-Projekt';
const SETTING_KEY = 'resourceImport';

export default {
  name: 'PatchlistUploader',
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    icons: {
      mdiTableColumn,
      mdiAlertCircle,
      mdiUpload,
      mdiFormatText,
      mdiNumeric
    },
    showDialog: false,
    showSubmitDialog: false,
    isSubmitting: false,
    isReadingFile: false,
    file: null,
    truncateLength: 0,
    errorType: null,
    errorMessage: null,
    parsedFile: null,
    // table variables
    tableHeaders: [
      { text: 'AGS', value: 'ags' },
      { text: 'Vertragsnummer', value: 'contractId' },
      { text: 'Home ID', value: 'homeId' },
      { text: 'ONT Seriennummer', value: 'ontSerialNumber' },
      { text: 'MFG', value: 'mfg' },
      { text: 'MFG Splice Panel', value: 'mfgSplicePanel' },
      { text: 'MFG Panel Port', value: 'mfgPanelPort' },
      { text: 'MSAN Hostname', value: 'deviceHostname' },
      { text: 'MSAN Slot', value: 'deviceSlot' },
      { text: 'MSAN Port', value: 'devicePort' }
    ],
    headerDefaults: {
      ags: { type: String, value: 'G-AGS-Key' },
      contractId: { type: Number, value: 'Vertragsnummer' },
      homeId: { type: String, value: 'Home-ID' },
      ontSerialNumber: { type: String, value: 'ONT_SN' },
      mfg: { type: String, value: 'MFG' },
      mfgSplicePanel: { type: String, value: 'MFG_Spleißpanel' },
      mfgPanelPort: { type: String, value: 'MFG_Panelport' },
      deviceHostname: {
        type: String,
        optional: true,
        value: 'Device_Hostname'
      },
      deviceSlot: { type: Number, value: 'Device_Slot' },
      devicePort: { type: Number, value: 'Device_Port' }
    },
    tableItemsPerPage: 15,
    showInvalidOnly: false,
    tableSearchTerm: '',
    selectedHeaders: null,
    availableHeaders: null,
    freeHeaders: null,
    selectedHeader: null,
    selectHeaderPromise: null,
    columnNameForSelection: null,
    // excel variables
    selectedSheetValue: null,
    selectSheetPromise: null,
    selectSheetOptions: null
  }),
  computed: {
    tableItems() {
      if (this.isAllHeadersMissing) return [];
      const headers = this.selectedHeaders;
      return this.parsedFile
        ?.map((i) => {
          const item = {};
          let isInvalid = !this.showInvalidOnly;
          for (const key in headers) {
            let value = headers[key] ? i[headers[key]] : null;

            const type = this.headerDefaults[key]?.type;
            if (type === String && !!value) {
              value = value.toString();
            } else if (type === Number && !!value) {
              if (typeof value === 'string') {
                value = parseInt(value);
                if (isNaN(value)) {
                  value = null;
                }
              }
            } else {
              value = null;
            }

            isInvalid = isInvalid || !this.isValueValid(value, key)[0];

            item[key] = value;
          }
          return isInvalid ? item : null;
        })
        .filter((i) => i);
    },
    allowedExtensions() {
      return [EXCEL_EXTENSION, CSV_EXTENSION]; // for now only excel files are supported
    },
    requiredHeaders() {
      return this.tableKeys.map((k) => this.headerDefaults[k].value);
    },
    isAllHeadersMissing() {
      return this.requiredHeaders?.length === this.missingHeaders?.length;
    },
    missingHeaders() {
      return (
        this.selectedHeaders &&
        Object.entries(this.selectedHeaders)
          .map(([key, value]) => {
            if (!value) {
              return this.headerDefaults[key].value;
            }
          })
          .filter((e) => e)
      );
    },
    tableKeys() {
      return Object.keys(this.headerDefaults);
    },
    headerDefaultMap() {
      return this.tableKeys.reduce(
        (map, key) => map.set(key, this.headerDefaults[key]),
        new Map()
      );
    },
    errors() {
      if (!this.parsedFile || !this.tableItems) return [];
      const errors = [];

      errors.push(
        ...this.getErrorsForField(
          'contractId',
          (contractId, count) =>
            `Für den Vertrag <b>${contractId}</b> befinden sich ${count} Einträge in der Liste`,
          (count) => `${count} Einträge haben keine Vertragsnummer`
        )
      );

      errors.push(
        ...this.getErrorsForField(
          'homeId',
          (homeId, count) =>
            `${count} Einträge haben die Home ID <b>${homeId}</b>`,
          (count) => `${count} Einträge haben keine Home ID`
        )
      );

      errors.push(
        ...this.getErrorsForField(
          'ontSerialNumber',
          (ontSerialNumber, count) =>
            `Die ONT Seriennummer <b>${ontSerialNumber}</b> befinden sich ${count} mal in der Liste`,
          (count) => `${count} Einträge haben keine ONT Seriennummer`
        )
      );

      return errors;
    },
    isTableDataValid() {
      if (
        this.errors.length > 0 ||
        !this.tableItems ||
        this.tableItems.length === 0
      ) {
        return false;
      }
      for (const item of this.tableItems) {
        for (const key of this.tableKeys) {
          const [isValueValid, isOptional] = this.isValueValid(item[key], key);
          if (!isOptional && !isValueValid) {
            return false;
          }
        }
      }
      return true;
    },
    tableItemsCount() {
      return this.tableItems ? this.tableItems.length : 0;
    }
  },
  watch: {
    missingHeaders: {
      handler: function (missingHeaders) {
        if (missingHeaders && missingHeaders.length > 0) {
          this.setWarningMessage(
            `Das Dokument hat nicht alle benötigten Spalten; <code>${this.extensionJoin(
              missingHeaders
            )}`,
            false
          );
        } else if (
          this.errorMessage !== null &&
          this.errorMessage.startsWith(
            'Das Dokument hat nicht alle benötigten Spalten'
          )
        ) {
          this.setErrorMessage(null, 'error', false);
        }
      },
      deep: true
    },
    showDialog: function (newVal) {
      if (newVal && !this.parsedFile) {
        setTimeout(() => {
          // open file selection the first time
          this.$refs.filePicker.$refs.input.click();
        });
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
    setTimeout(this.onResize, 1);

    const settings = this.$settings.get(SETTING_KEY, {});
    this.tableItemsPerPage = settings?.itemsPerPage ?? 15;
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    onResize() {
      const el = this.$refs.filePicker?.$el;
      if (el) {
        this.truncateLength = Math.round(
          (el.getBoundingClientRect().width - 180) / 12
        );
      }
    },
    async onFileChange(file) {
      if (!file) return;
      const reader = new FileReader();
      const self = this;
      this.parsedFile = null;
      this.errorMessage = null;
      this.isReadingFile = true;

      reader.onload = async function (e) {
        try {
          const data = e.target.result;
          if (file.name.endsWith(EXCEL_EXTENSION)) {
            return await self.parseExcel(data);
          } else if (file.name.endsWith(CSV_EXTENSION)) {
            return await self.parseCsv(data);
          }
        } catch (ex) {
          console.error('Error parsing file', ex);
          return self.setErrorMessage(
            'Das ausgewählte Dokument konnte nicht gelesen werden'
          );
        } finally {
          self.isReadingFile = false;
        }

        self.errorMessage = `Ungültiges Dateiformat. Unterstützt werden nur die Dateiformate <code>${self.extensionJoin(
          self.allowedExtensions
        )}</code> unterstützt.`;
      };
      reader.readAsBinaryString(file);
    },
    isValueValid(value, key) {
      if (!key) return [false, false];
      const { type, optional } = this.headerDefaultMap.get(key);
      if (!value || !type) return [false, optional];
      if (type === String) {
        return [
          typeof value === 'string' && value.length > 0,
          optional ?? false
        ];
      }

      if (type === Number) {
        return [typeof value === 'number' && !isNaN(value), optional ?? false];
      }

      return [false, optional];
    },
    async parseExcel(blob) {
      const workbook = XLSX.read(blob, { type: 'binary' });

      const worksheet = await this.getWorksheet(workbook);
      if (!worksheet) {
        return this.setErrorMessage(
          'Das Excel Dokument konnte nicht gelesen werden'
        );
      }

      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        blankrows: false
      });
      if (!jsonData || Object.values(jsonData).length <= 0) {
        return this.setErrorMessage('Das Dokument enthält keine Einträge');
      }

      const headers = Object.keys(jsonData.at(0));

      this.availableHeaders = headers;
      this.freeHeaders = [...this.availableHeaders];
      this.selectedHeaders = this.tableKeys.reduce((o, c) => {
        const v = this.headerDefaults[c];
        o[c] = headers.includes(v.value) ? v.value : null;
        return o;
      }, {});
      this.freeHeaders = this.freeHeaders.filter(
        (h) => !Object.values(this.selectedHeaders).includes(h)
      );

      this.parsedFile = jsonData;
    },
    async parseCsv(blob) {
      const delimiter = ',';
      const buffer = Buffer.from(blob, 'binary');
      const decoder = new TextDecoder('utf-8');
      const fileContent = decoder.decode(buffer);
      const allRows = fileContent.split(/[\r\n]+/);

      const headers = allRows
        .at(0)
        .split(delimiter)
        .filter((i) => i.trim() !== '');

      this.availableHeaders = headers;
      this.freeHeaders = [...this.availableHeaders];
      this.selectedHeaders = this.tableKeys.reduce((o, c) => {
        const v = this.headerDefaults[c];
        o[c] = headers.includes(v.value) ? v.value : null;
        return o;
      }, {});

      const rows = allRows.slice(1).map((row) => {
        const parts = row.split(delimiter);
        const item = {};
        for (const h of headers) {
          item[h] = parts[headers.indexOf(h)];
        }
        return item;
      });

      this.freeHeaders = this.freeHeaders.filter(
        (h) => !Object.values(this.selectedHeaders).includes(h)
      );

      this.parsedFile = rows;
    },
    async getWorksheet(workbook) {
      if (workbook.SheetNames.length === 0) {
        return null;
      }

      if (workbook.SheetNames.length > 1) {
        return await this.selectWorksheet(workbook);
      }

      const sheetName = workbook.SheetNames[0];
      return workbook.Sheets[sheetName];
    },
    async selectWorksheet(workbook) {
      const defaultSheetName = DEFAULT_SHEET_NAME;
      const defaultNameIndex = workbook.SheetNames.findIndex(
        (n) => n === defaultSheetName
      );

      if (defaultNameIndex !== -1) {
        return workbook.Sheets[defaultSheetName];
      }

      this.selectSheetOptions = workbook.SheetNames;

      const selectedSheetName = await new Promise((resolve) => {
        this.selectSheetPromise = resolve;
      });

      const selectedNameIndex = workbook.SheetNames.findIndex(
        (n) => n === selectedSheetName
      );

      if (selectedNameIndex !== -1) {
        return workbook.Sheets[selectedSheetName];
      }

      return null;
    },
    userSelectWorksheet() {
      this.selectSheetPromise(this.selectedSheetValue);
      this.selectedSheetPromise = null;
      this.selectSheetOptions = null;
    },
    async selectHeaderForCol(col) {
      this.columnNameForSelection = this.tableHeaders.find(
        (h) => h.value === col
      )?.text;
      const selectedHeader = await new Promise((resolve) => {
        this.selectHeaderPromise = resolve;
      });
      if (!selectedHeader) return;
      const old = this.selectedHeaders[col];
      this.selectedHeaders[col] = selectedHeader;
      this.freeHeaders.push(old);
      this.freeHeaders = this.freeHeaders.filter(
        (h) => !!h && h !== selectedHeader
      );
    },
    userSelectHeaderForCol() {
      this.selectHeaderPromise(this.selectedHeader);
      this.selectHeaderPromise = null;
      this.selectedHeader = null;
    },
    extensionJoin(arr) {
      if (arr.length === 0) return '';
      if (arr.length === 1) return arr[0];
      if (arr.length === 2) return arr.join('</code> und <code>');

      return [
        arr.slice(0, -2).join('</code>, <code>'),
        arr.slice(-2).join('</code> und <code>')
      ].join('</code>, <code>');
    },
    submitData() {
      if (!this.isTableDataValid) return;
      this.isSubmitting = true;

      const data = {
        projectId: this.project.projektId,
        items: this.tableItems
      };

      HTTP.put('gncReport/resourceImport', data)
        .then((res) => {
          this.showMessage('Der import wurde erfolgreich gestartet');
          this.$emit('submitSuccessful');
          this.file = null;
          this.parsedFile = null;
          this.showDialog = false;
        })
        .catch(({ response: { status, data } }) => {
          this.showMessage(`Status ${status}: ${data}`, 'error');
        })
        .finally(() => {
          this.$emit('submitSuccessful');
          this.showSubmitDialog = false;
          this.isSubmitting = false;
        });
    },
    getIconByType(header) {
      const headerType = this.headerDefaults[header.value]?.type ?? String;
      switch (headerType) {
        case Number:
          return this.icons.mdiNumeric;
        case String:
        default:
          return this.icons.mdiFormatText;
      }
    },
    setWarningMessage(message, clearFile = false) {
      this.setErrorMessage(message, 'warning', clearFile);
    },
    setErrorMessage(message, errorType = 'error', clearFile = true) {
      this.errorType = errorType;
      this.errorMessage = message;
      if (clearFile) {
        this.file = null;
      }
    },
    showMessage(message, type = 'success') {
      this.$store.commit(
        'addNotification',
        new NotificationObject(type, message)
      );
    },
    updateLocalStorage: function (key, value) {
      this.$settings.set(`${SETTING_KEY}.${key}`, value);
    },
    getErrorsForField(field, multipleFn, nullFn) {
      if (!field || this.selectedHeaders[field] === null) return [];
      const errors = [];

      const fieldValues = this.tableItems.map((e) => e[field]);
      const valueCounts = {};

      for (const fieldValue of fieldValues) {
        valueCounts[fieldValue] = valueCounts[fieldValue]
          ? valueCounts[fieldValue] + 1
          : 1;
      }

      for (const fieldValue of Object.keys(valueCounts)) {
        if (valueCounts[fieldValue] > 1) {
          if (fieldValue === 'null') {
            errors.push(nullFn(valueCounts[fieldValue]));
          } else {
            errors.push(multipleFn(fieldValue, valueCounts[fieldValue]));
          }
        }
      }

      return errors;
    }
  }
};
</script>

<style scoped>
.scrollable-table {
  max-height: 300px;
  display: flex;
  flex-direction: column;
}

.scrollable-body {
  overflow-y: auto;
  display: block;
  max-height: 200px;
}
.scrollable-body tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}
</style>
